import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Info, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface StreakCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

const StreakCalendarModal: React.FC<StreakCalendarModalProps> = ({ isOpen, onClose, userId }) => {
  const { t } = useTranslation();
  const [completedDays, setCompletedDays] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [dataVisualizada, setDataVisualizada] = useState(new Date());

  const now = new Date();
  const currentYear = dataVisualizada.getFullYear();
  const currentMonth = dataVisualizada.getMonth();
  const isCurrentMonth = currentYear === now.getFullYear() && currentMonth === now.getMonth();
  const todayDate = isCurrentMonth ? now.getDate() : -1;
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  const handlePrevMonth = () => {
    setDataVisualizada(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    if (isCurrentMonth) return;
    setDataVisualizada(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
  };

  const monthNames = [
    t('common.january', 'Janeiro'), t('common.february', 'Fevereiro'), t('common.march', 'Março'),
    t('common.april', 'Abril'), t('common.may', 'Maio'), t('common.june', 'Junho'),
    t('common.july', 'Julho'), t('common.august', 'Agosto'), t('common.september', 'Setembro'),
    t('common.october', 'Outubro'), t('common.november', 'Novembro'), t('common.december', 'Dezembro')
  ];

  const weekDays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

  useEffect(() => {
    if (isOpen && userId) {
      // Unilateral cache invalidation for database updates
      if (localStorage.getItem('libritui_calendar_cache_cleared_v2') !== 'true') {
        const cachePrefix = `libritui_calendar_cache_${userId}_`;
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(cachePrefix)) {
            localStorage.removeItem(key);
          }
        });
        localStorage.setItem('libritui_calendar_cache_cleared_v2', 'true');
      }

      const monthPrefix = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
      const cacheKey = `libritui_calendar_cache_${userId}_${monthPrefix}`;
      const cached = localStorage.getItem(cacheKey);

      let hasCache = false;
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed)) {
            setCompletedDays(new Set(parsed));
            setIsLoading(false);
            hasCache = true;
          }
        } catch (e) {
          console.error("Failed to parse calendar cache", e);
        }
      }

      // If it is a past month AND we have cache, do NOT fetch from D1 at all (load instantly)
      if (hasCache && !isCurrentMonth) {
        return;
      }

      if (!hasCache) {
        setIsLoading(true);
      }

      fetch('/api/db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get_calendar_streaks',
          user_id: userId,
          month_prefix: monthPrefix
        })
      })
      .then(res => res.json())
      .then(data => {
        if (data && data.data && Array.isArray(data.data)) {
          const monthRecords = data.data.filter((r: any) => r.data_conclusao?.startsWith(monthPrefix));
          
          const completedSet = new Set<number>();
          monthRecords.forEach((r: any) => {
            const day = parseInt(r.data_conclusao.substring(8, 10), 10);
            completedSet.add(day);
          });
          setCompletedDays(completedSet);
          localStorage.setItem(cacheKey, JSON.stringify(Array.from(completedSet)));
        }
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Failed to load historico_streaks", err);
        setIsLoading(false);
      });
    }
  }, [isOpen, userId, currentYear, currentMonth, isCurrentMonth]);

  const y = completedDays.size;
  const x = daysInMonth;
  const highConsistency = y >= (x / 2);

  const motivationalMessage = highConsistency
    ? t('common.streak_motivational_high', 'Ritmo fantástico! Suas aventuras literárias estão construindo um hábito sólido. Continue assim!')
    : t('common.streak_motivational_low', 'Cada página conta! Que tal abrir seu livro favorito por alguns minutos hoje para acender o marcador de amanhã?');

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/30 backdrop-blur-md animate-in fade-in duration-300 pointer-events-auto" onClick={onClose}>
          <motion.div 
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-full max-w-sm flex flex-col items-center relative pointer-events-auto overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="w-full px-6 pt-8 pb-4 flex items-center justify-between relative">
              <button onClick={() => setShowInfo(true)} className="p-2 text-muted hover:text-primary transition-colors rounded-full hover:bg-tint-stone/60">
                <Info size={20} />
              </button>
              <div className="flex items-center gap-2 absolute left-1/2 -translate-x-1/2">
                <button onClick={handlePrevMonth} className="p-1 text-muted hover:text-primary transition-colors cursor-pointer rounded-full hover:bg-tint-stone/60">
                  <ChevronLeft size={20} />
                </button>
                <h2 className="text-xl font-semibold text-ink capitalize text-center min-w-[120px]">
                  {monthNames[currentMonth]} {currentYear}
                </h2>
                <button onClick={handleNextMonth} disabled={isCurrentMonth} className={`p-1 transition-colors rounded-full ${isCurrentMonth ? 'text-muted/60 cursor-default' : 'text-muted hover:text-primary cursor-pointer hover:bg-tint-stone/60 '}`}>
                  <ChevronRight size={20} />
                </button>
              </div>
              <button onClick={onClose} className="p-2 text-muted hover:text-ink transition-colors rounded-full hover:bg-tint-stone/60">
                <X size={20} />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="w-full px-8 pb-4">
              {isLoading ? (
                <div className="flex justify-center items-center h-[200px]">
                  <Flame className="animate-pulse text-warn/50" size={32} />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-7 gap-y-3 gap-x-2 text-center mb-2">
                    {weekDays.map((day, i) => (
                      <div key={i} className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em]">{day}</div>
                    ))}
                    {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                      <div key={`empty-${i}`} />
                    ))}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const dayNumber = i + 1;
                      const isCompleted = completedDays.has(dayNumber);
                      const isToday = dayNumber === todayDate;
                      return (
                        <div key={`day-${dayNumber}`} className="flex flex-col items-center justify-center relative h-10 w-full">
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-all ${
                            isCompleted 
                              ? 'bg-tint-butter text-warn dark:bg-tint-butter/40 dark:text-warn shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)]' 
                              : isToday
                                ? 'border border-primary text-primary dark:text-primary dark:border-primary/80'
                                : 'text-muted '
                          }`}>
                            {dayNumber}
                          </div>
                          {isCompleted && (
                            <div className="absolute -bottom-1">
                              <Flame size={10} className="text-warn" fill="currentColor" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Motivation Panel */}
            <div className="w-full bg-tint-stone/40 p-6 rounded-t-3xl border-t border-black/[0.06] dark:border-white/[0.06] flex flex-col items-center text-center mt-2">
              <div className="bg-surface px-4 py-2 rounded-full border border-black/[0.06] dark:border-white/[0.06] mb-4 inline-flex items-center gap-2">
                <Flame size={16} className="text-warn" fill="currentColor" />
                <span className="text-sm font-bold text-ink">
                  {t('common.streak_month_summary', 'Neste mês, você estudou {{y}} dias de {{x}} possíveis!', { y, x })}
                </span>
              </div>
              <p className="text-xs text-muted leading-relaxed font-medium">
                {motivationalMessage}
              </p>
            </div>

            {/* Info Balloon Overlay */}
            <AnimatePresence>
              {showInfo && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute inset-0 bg-surface/95 backdrop-blur-sm z-10 p-8 flex flex-col items-center justify-center text-center"
                >
                  <button onClick={() => setShowInfo(false)} className="absolute top-6 right-6 p-2 text-muted hover:text-ink transition-colors">
                    <X size={24} />
                  </button>
                  <Flame size={48} className="text-warn mb-6" fill="currentColor" />
                  <h3 className="text-xl font-semibold text-ink mb-4">
                    {t('common.what_are_streaks', 'O que são as Ofensivas?')}
                  </h3>
                  <p className="text-sm text-muted mb-8 leading-relaxed">
                    {t('common.streak_explanation', 'As ofensivas (streaks) mostram quantos dias você se dedicou aos seus estudos!')}
                  </p>
                  
                  <div className="space-y-4 text-left w-full max-w-[240px]">
                    <div className="flex items-start gap-3">
                      <div className="min-w-5 h-5 rounded-full bg-ok/20 text-ok flex items-center justify-center mt-0.5 text-[10px]">✓</div>
                      <p className="text-xs text-muted font-medium leading-relaxed">{t('common.streak_rule_1', 'Leia qualquer livro por 3 minutos seguidos.')}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="min-w-5 h-5 rounded-full bg-ok/20 text-ok flex items-center justify-center mt-0.5 text-[10px]">✓</div>
                      <p className="text-xs text-muted font-medium leading-relaxed">{t('common.streak_rule_2', 'Pratique pelo menos uma vez em escrita, pronúncia ou flashcards.')}</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default StreakCalendarModal;
